import styled from "styled-components"
import Header from "../Common/Header"
import Footer from "../Common/Footer"
import ConsultationForm from "@components/forms/ConsultationForm"
import imgMrcRegion from "@assets/img/mrc_region.jpg"

const PageWrapper = styled.div`
  background: #eef3f7;
  color: #172c42;
`

const HeroSection = styled.section`
  position: relative;
  min-height: 720px;
  display: flex;
  align-items: flex-end;
  padding: clamp(4rem, 8vw, 7rem) 0;
  isolation: isolate;
  overflow: hidden;
  color: #ffffff;
  background: #071b30 url("/img/modern-apartment.jpg") center 48% / cover no-repeat;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background: linear-gradient(90deg, rgba(5, 20, 36, 0.97) 0%, rgba(8, 30, 52, 0.9) 48%, rgba(8, 30, 52, 0.48) 100%);
  }

  @media (max-width: 800px) {
    min-height: auto;
  }
`

const HeroInner = styled.div`
  width: min(1280px, 90vw);
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(300px, 0.55fr);
  gap: clamp(3rem, 8vw, 8rem);
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`

const HeroKicker = styled.p`
  margin: 0 0 1rem;
  color: #92c9f4;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.17em;
  text-transform: uppercase;
`

const HeroTitle = styled.h1`
  max-width: 900px;
  color: #ffffff;
  font-size: clamp(3rem, 6vw, 5.8rem);
  line-height: 0.94;
  letter-spacing: -0.06em;
`

const HeroSubtitle = styled.p`
  max-width: 720px;
  margin: 1.75rem 0 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: clamp(1.05rem, 1.7vw, 1.24rem);
  line-height: 1.7;
`

const HeroSpecs = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.28);
  border-bottom: 1px solid rgba(255, 255, 255, 0.28);

  span {
    padding: 0.75rem 1.1rem;
    border-right: 1px solid rgba(255, 255, 255, 0.28);
    color: rgba(255, 255, 255, 0.76);
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
`

const HeroCTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
`

const PrimaryCTA = styled.a`
  min-height: 54px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 1.5rem;
  border: 1px solid #ffffff;
  border-radius: 3px;
  background: #ffffff;
  color: #0d365d;
  font-weight: 800;
  text-decoration: none;
  transition:
    background 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: transparent;
    color: #ffffff;
  }
`

const SecondaryCTA = styled.a`
  min-height: 54px;
  display: inline-flex;
  align-items: center;
  padding: 0 1rem;
  color: rgba(255, 255, 255, 0.84);
  font-weight: 700;
  text-decoration: none;

  &::after {
    content: "↘";
    margin-left: 0.65rem;
  }

  &:hover {
    color: #ffffff;
  }
`

const HeroLedger = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.42);
`

const LedgerItem = styled.div`
  padding: 1.25rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.26);

  h2 {
    color: #ffffff;
    font-size: 1rem;
    letter-spacing: -0.01em;
  }

  p {
    margin: 0.45rem 0 0;
    color: rgba(255, 255, 255, 0.68);
    font-size: 0.9rem;
    line-height: 1.55;
  }
`

const ContentWrapper = styled.div`
  padding: clamp(5rem, 10vw, 9rem) 0;
  display: grid;
  gap: clamp(6rem, 12vw, 11rem);
`

const Section = styled.section`
  width: min(1280px, 90vw);
  margin: 0 auto;
`

const SectionHeader = styled.div`
  display: grid;
  grid-template-columns: 170px minmax(0, 760px);
  gap: 2rem;
  margin-bottom: clamp(2.5rem, 6vw, 5rem);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`

const SectionLabel = styled.p`
  margin: 0;
  color: #337abd;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`

const SectionTitle = styled.h2`
  color: #102f4f;
  font-size: clamp(2.5rem, 5vw, 4.7rem);
  line-height: 0.98;
  letter-spacing: -0.055em;
`

const MandateGrid = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-top: 1px solid #aebfce;
  counter-reset: mandate;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`

const MandateItem = styled.li`
  min-height: 190px;
  padding: 1.5rem 2rem 2rem 0;
  border-bottom: 1px solid #aebfce;
  counter-increment: mandate;

  &:nth-child(odd) {
    border-right: 1px solid #aebfce;
  }

  &:nth-child(even) {
    padding-left: 2rem;
  }

  &::before {
    content: "0" counter(mandate);
    display: block;
    margin-bottom: 1.5rem;
    color: #337abd;
    font-size: 0.76rem;
    font-weight: 800;
    letter-spacing: 0.12em;
  }

  p {
    max-width: 500px;
    margin: 0;
    color: #354c61;
    font-size: 1.05rem;
    line-height: 1.65;
  }

  @media (max-width: 760px) {
    min-height: auto;
    padding: 1.5rem 0 2rem !important;
    border-right: 0 !important;
  }
`

const CoveragePanel = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  background: #ffffff;
  border: 1px solid #b9c9d6;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const CoverageCopy = styled.div`
  padding: clamp(2rem, 5vw, 4.5rem);
  border-right: 1px solid #b9c9d6;

  h2 {
    color: #102f4f;
    font-size: clamp(2.2rem, 4vw, 3.8rem);
    line-height: 1;
    letter-spacing: -0.05em;
  }

  > p {
    margin: 1.5rem 0 0;
    color: #52687c;
    line-height: 1.7;
  }

  @media (max-width: 900px) {
    border-right: 0;
    border-bottom: 1px solid #b9c9d6;
  }
`

const CoverageList = styled.ul`
  list-style: none;
  margin: 2rem 0 0;
  padding: 0;
  border-top: 1px solid #d0dbe4;

  li {
    padding: 0.85rem 0;
    border-bottom: 1px solid #d0dbe4;
    color: #354c61;
    line-height: 1.5;
  }
`

const CTAButton = styled.a`
  min-height: 52px;
  display: inline-flex;
  align-items: center;
  margin-top: 2rem;
  padding: 0 1.3rem;
  border-radius: 3px;
  background: #175f98;
  color: #ffffff;
  font-weight: 800;
  text-decoration: none;

  &:hover {
    background: #0d4172;
  }
`

const CoverageImage = styled.figure`
  margin: 0;
  min-height: 520px;
  background: #e8edf1;

  img {
    width: 100%;
    height: 100%;
    min-height: 520px;
    display: block;
    object-fit: cover;
  }
`

const MethodSection = styled.section`
  background: #091e34;
  color: #ffffff;
`

const MethodInner = styled.div`
  width: min(1280px, 90vw);
  margin: 0 auto;
  padding: clamp(4rem, 9vw, 8rem) 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.7fr);
  gap: clamp(3rem, 9vw, 8rem);

  @media (max-width: 840px) {
    grid-template-columns: 1fr;
  }
`

const MethodCopy = styled.div`
  h2 {
    max-width: 700px;
    color: #ffffff;
    font-size: clamp(2.5rem, 5vw, 4.7rem);
    line-height: 0.98;
    letter-spacing: -0.055em;
  }

  > p {
    max-width: 700px;
    margin: 1.5rem 0 0;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.75;
  }
`

const MethodList = styled.ul`
  list-style: none;
  margin: 2rem 0 0;
  padding: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.24);

  li {
    padding: 1rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.24);
    color: rgba(255, 255, 255, 0.8);
  }
`

const MethodNote = styled.aside`
  align-self: end;
  padding: 1.75rem 0 0 1.75rem;
  border-top: 1px solid #5aa9e6;
  border-left: 1px solid #5aa9e6;
  color: rgba(255, 255, 255, 0.76);
  font-size: 1.03rem;
  line-height: 1.7;
`

const FormSection = styled.section`
  width: min(1280px, 90vw);
  margin: 0 auto;
`

const FAQGroup = styled.div`
  border-top: 1px solid #aebfce;
`

const FAQItem = styled.details`
  border-bottom: 1px solid #aebfce;

  summary {
    position: relative;
    padding: 1.4rem 3rem 1.4rem 0;
    color: #173a5d;
    font-size: 1.05rem;
    font-weight: 800;
    cursor: pointer;
    list-style: none;
  }

  summary::-webkit-details-marker {
    display: none;
  }

  summary::after {
    content: "+";
    position: absolute;
    right: 0;
    color: #337abd;
    font-size: 1.4rem;
    font-weight: 400;
  }

  &[open] summary::after {
    content: "−";
  }

  p {
    max-width: 850px;
    margin: 0 0 1.5rem;
    color: #52687c;
    line-height: 1.7;
  }
`

const GestionLocativePortneuf = () => (
  <PageWrapper>
    <Header />
    <main id="contenu-principal">
      <HeroSection>
        <HeroInner>
          <div>
            <HeroKicker>Gestion locative · Portneuf et Québec Ouest</HeroKicker>
            <HeroTitle>Déléguer le quotidien. Garder le contrôle.</HeroTitle>
            <HeroSubtitle>
              Confiez vos immeubles de 2 à 30 portes à une équipe locale qui coordonne les locataires, les fournisseurs et les suivis administratifs
              convenus avec vous.
            </HeroSubtitle>
            <HeroSpecs>
              <span>2 à 30 portes</span>
              <span>Suivi documenté</span>
              <span>Urgences 7 j / 7</span>
            </HeroSpecs>
            <HeroCTAGroup>
              <PrimaryCTA href="#consultation">Demander une consultation</PrimaryCTA>
              <SecondaryCTA href="#coverage">Voir la zone desservie</SecondaryCTA>
            </HeroCTAGroup>
          </div>
          <HeroLedger>
            <LedgerItem>
              <h2>Sélection documentée</h2>
              <p>Vérifications prévues au processus, avec le consentement du candidat.</p>
            </LedgerItem>
            <LedgerItem>
              <h2>Maintenance préventive</h2>
              <p>Visites planifiées, carnet d’entretien et coordination des fournisseurs.</p>
            </LedgerItem>
            <LedgerItem>
              <h2>Suivi financier clair</h2>
              <p>Perception, renouvellements et rapports selon le mandat retenu.</p>
            </LedgerItem>
          </HeroLedger>
        </HeroInner>
      </HeroSection>

      <ContentWrapper>
        <Section>
          <SectionHeader>
            <SectionLabel>Le mandat</SectionLabel>
            <SectionTitle>Une portée définie dès le départ.</SectionTitle>
          </SectionHeader>
          <MandateGrid>
            <MandateItem>
              <p>Une équipe basée à Neuville qui dessert la MRC de Portneuf et la couronne ouest de Québec.</p>
            </MandateItem>
            <MandateItem>
              <p>Des outils comme Proprio Expert et Otonom Solutions pour la perception, la communication et les suivis techniques.</p>
            </MandateItem>
            <MandateItem>
              <p>Des paramètres d’autorisation et une portée de mandat convenus avant le début des opérations.</p>
            </MandateItem>
            <MandateItem>
              <p>Des rapports, des photos après les interventions pertinentes et une ligne d’urgence 7 jours sur 7.</p>
            </MandateItem>
          </MandateGrid>
        </Section>

        <Section id="coverage">
          <CoveragePanel>
            <CoverageCopy>
              <SectionLabel>Territoire</SectionLabel>
              <h2>Une présence dans votre secteur.</h2>
              <p>
                Nous gérons les immeubles de Neuville, Donnacona, Cap-Santé, Pont-Rouge, Portneuf et la couronne ouest de Québec, dont
                Saint-Augustin-de-Desmaures.
              </p>
              <CoverageList>
                <li>Positionnement locatif selon la municipalité.</li>
                <li>Coordination avec les services municipaux et les inspections locales.</li>
                <li>Demandes d’estimation et suivi des fournisseurs retenus.</li>
                <li>Communication avec les parties prenantes du dossier.</li>
              </CoverageList>
              <CTAButton href="#consultation">Vérifier la disponibilité</CTAButton>
            </CoverageCopy>
            <CoverageImage>
              <img src={imgMrcRegion} alt="Carte de la MRC de Portneuf et de la couronne ouest de Québec" loading="lazy" />
            </CoverageImage>
          </CoveragePanel>
        </Section>

        <MethodSection>
          <MethodInner>
            <MethodCopy>
              <SectionLabel>Suivi opérationnel</SectionLabel>
              <h2>Les faits utiles, au bon moment.</h2>
              <p>
                Les inspections prévues sont planifiées, les incidents sont centralisés et les dépenses non urgentes sont soumises à votre approbation
                selon les paramètres convenus.
              </p>
              <MethodList>
                <li>Tableaux de bord et rapports selon le niveau de service retenu.</li>
                <li>Procédures d’urgence documentées et équipe de garde locale.</li>
                <li>Renouvellements, avis et actions de mise en location suivis au calendrier.</li>
              </MethodList>
            </MethodCopy>
            <MethodNote>
              Le mandat précise qui autorise les dépenses, comment les urgences sont traitées, quels rapports sont remis et à quelle fréquence.
            </MethodNote>
          </MethodInner>
        </MethodSection>

        <FormSection id="consultation">
          <ConsultationForm
            inquiryType="Gestion locative Portneuf et la couronne ouest de Québec"
            title="Entrer en contact"
            description="Prenons 15 minutes pour comprendre votre parc immobilier, les tâches que vous souhaitez déléguer et le niveau de suivi attendu."
            additionalFields={[
              {
                name: "propertyProfile",
                label: "Décrivez votre portefeuille immobilier",
                type: "select",
                options: [
                  { label: "Petit plex familial (4-6 logements)", value: "Plex 4-6 logements" },
                  { label: "Immeuble de taille moyenne (7-15 logements)", value: "Immeuble 7-15 logements" },
                  { label: "Grand immeuble (16-30 logements)", value: "Immeuble 16-30 logements" },
                  { label: "Propriétés mixtes", value: "Mixte résidentiel/commercial" },
                ],
              },
              {
                name: "desiredScope",
                label: "Quel volet souhaitez-vous surtout déléguer?",
                type: "select",
                options: [
                  { label: "Gestion locative complète", value: "Gestion locative complète" },
                  { label: "Relations avec les locataires", value: "Relations locataires" },
                  { label: "Administration et suivis", value: "Administration et suivis" },
                  { label: "Entretien et coordination des travaux", value: "Entretien et travaux" },
                  { label: "Transition depuis une gestion actuelle", value: "Transition de gestion" },
                ],
              },
              {
                name: "currentChallenges",
                label: "Principaux défis ou préoccupations",
                type: "textarea",
                placeholder: "Locataires, paiements, urgences, entretien préventif, manque de temps…",
                fullWidth: true,
              },
            ]}
          />
        </FormSection>

        <Section>
          <SectionHeader>
            <SectionLabel>Questions fréquentes</SectionLabel>
            <SectionTitle>Avant de commencer.</SectionTitle>
          </SectionHeader>
          <FAQGroup>
            <FAQItem>
              <summary>Quelle durée de mandat proposez-vous?</summary>
              <p>
                Nous recommandons un mandat d’au moins 12 mois pour assurer une transition stable et planifier les échéances. Les conditions de
                renouvellement sont précisées au contrat.
              </p>
            </FAQItem>
            <FAQItem>
              <summary>Quel est votre processus de sélection des locataires?</summary>
              <p>
                Avec l’autorisation du candidat, nous réalisons les vérifications prévues au processus. Les mêmes critères sont appliqués de façon
                cohérente à chaque candidature.
              </p>
            </FAQItem>
            <FAQItem>
              <summary>Gérez-vous aussi les travaux majeurs?</summary>
              <p>
                Nous pouvons coordonner les demandes d’estimation, présenter les budgets pour approbation et suivre les travaux selon les paramètres
                du mandat.
              </p>
            </FAQItem>
          </FAQGroup>
        </Section>
      </ContentWrapper>
    </main>
    <Footer />
  </PageWrapper>
)

export default GestionLocativePortneuf
